import React, { useState } from 'react'
import { Tabs, Tab } from '@mui/material'
import DashboardHistoryPanel from './DashboardHistoryPanel'
import DashboardSignOffHistoryPanel from './DashboardSignOffHistoryPanel'
import { DashboardSignOff, DashboardSignOffRevoked } from '../../types/gotypes'

// #974 — "Text edits", not "Edits". Text edits are literally all this tab
// captures; the shorter label made people wonder what else had been edited and
// assume something was missing.
export const HISTORY_TEXT_EDITS_LABEL = 'Text edits'
export const HISTORY_SIGN_OFFS_LABEL = 'Sign-offs'

export type DashboardHistoryTabKey = 'edits' | 'signoff'

export interface DashboardHistoryContext {
  // Gates both panels' fetches, so History only loads while it is on screen.
  active: boolean
  customer: string
  dashboardId: string
  // Sign-off context: current state comes from the dashboard config the caller
  // already holds; the event history is fetched by the sign-off panel.
  currentSignOff?: DashboardSignOff
  signOffRevoked?: DashboardSignOffRevoked
  widgetTitles?: Record<string, string>
}

/** The History sub-tab strip. Split out from the panel body so a Dialog can
 *  keep it fixed above its scrolling content while the Settings modal renders
 *  it inline — same labels, same order, one definition (#974). */
export const DashboardHistoryTabBar: React.FC<{
  value: DashboardHistoryTabKey
  onChange: (value: DashboardHistoryTabKey) => void
  px?: number
}> = ({ value, onChange, px = 0 }) => (
  <Tabs
    value={value}
    onChange={(_e, v) => onChange(v)}
    sx={{
      px,
      borderBottom: 1,
      borderColor: 'divider',
      minHeight: 40,
      '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
    }}
  >
    <Tab label={HISTORY_TEXT_EDITS_LABEL} value="edits" />
    <Tab label={HISTORY_SIGN_OFFS_LABEL} value="signoff" />
  </Tabs>
)

/** The body for the selected History sub-tab. */
export const DashboardHistoryTabPanel: React.FC<
  DashboardHistoryContext & { value: DashboardHistoryTabKey }
> = ({ value, active, customer, dashboardId, currentSignOff, signOffRevoked, widgetTitles }) =>
  value === 'edits' ? (
    <DashboardHistoryPanel active={active} customer={customer} dashboardId={dashboardId} />
  ) : (
    <DashboardSignOffHistoryPanel
      active={active}
      customer={customer}
      dashboardId={dashboardId}
      currentSignOff={currentSignOff}
      signOffRevoked={signOffRevoked}
      widgetTitles={widgetTitles}
    />
  )

/** The whole dashboard History surface (#974): "Text edits" and "Sign-offs"
 *  sub-tabs together. The preview-area History modal and the editor's
 *  Settings -> History tab used to disagree — Settings showed text edits only,
 *  with no sign-offs at all — so both now render this one pattern. */
const DashboardHistoryTabs: React.FC<DashboardHistoryContext> = (props) => {
  const [tab, setTab] = useState<DashboardHistoryTabKey>('edits')

  return (
    <>
      <DashboardHistoryTabBar value={tab} onChange={setTab} />
      <DashboardHistoryTabPanel {...props} value={tab} />
    </>
  )
}

export default DashboardHistoryTabs
